import { useCallback, useEffect, useState } from "react"
import FlexRow from "../../../components/base/FlexRow"
import Icon from "../../../components/base/Icon"
import { getUser, toPrettyDate, useHub } from "../../../core/Global"
import { toastError, toastSuccess } from "../../../components/base/Toast"

interface TechClientSession {
    id?: string
    techId?: string
    techName?: string
    techAvatarUrl?: string
    jobId?: string
    jobNumber?: string
    startedAt?: Date
}

interface IProps {
    chatId?: string
    /** Bumped whenever a message arrives, so a session that has just begun is noticed. */
    watch?: any
    /**
     * Called whenever the bar appears or disappears. The thread below is sized to the screen by
     * hand, so anything that takes height from it has to say when it did.
     */
    onToggle?: () => void
}

/**
 * Says when a technician is talking to this customer, and gives the office the one button that
 * stops it.
 *
 * Without it a customer's thread with a technician's messages in it looked exactly like one the
 * office had written itself — there was no way to tell who was speaking for the company, and no
 * way to call an end to it once the job was over.
 *
 * Shown to the office only. A technician reading the same conversation is reading their own
 * stretch of it and has no say in when it ends.
 */
export default function TechClientSessionBar(props: IProps) {

    const hub = useHub()
    const user = getUser()

    const [session, sessionSet] = useState<TechClientSession>()
    const [ending, endingSet] = useState<boolean>(false)

    const load = useCallback(() => {
        if (!props.chatId || user?.isTech) {
            sessionSet(undefined)
            return
        }

        hub.Get(`/crm/chat/techsession/${props.chatId}`, res => {
            sessionSet(res?.success ? res.data ?? undefined : undefined)
        })
    }, [props.chatId, user?.isTech])

    useEffect(() => { load() }, [load, props.watch])

    // Measured after it has been drawn, not while deciding to draw it.
    useEffect(() => {
        if (!props.onToggle) return
        const t = window.setTimeout(props.onToggle, 0)
        return () => window.clearTimeout(t)
    }, [!!session])

    if (!session) return <></>

    const end = () => {
        endingSet(true)
        hub.Post(`/crm/chat/techsession/end/${props.chatId}`, {}, res => {
            endingSet(false)
            if (!res?.success) {
                toastError(res?.message ?? "That chat could not be ended.")
                return
            }
            toastSuccess("The technician's chat with this customer has ended.")
            sessionSet(undefined)
        })
    }

    return (
        <div id="kt_chat_tech_session_bar"
            className="bg-light-primary border-bottom border-primary border-opacity-25 px-5 py-3">
            <FlexRow className="justify-content-between">
                <FlexRow>
                    <Icon icon="user-helmet-safety" className="text-primary me-3" size={16} />
                    <div>
                        <div className="fw-bold fs-7 text-gray-800">
                            {session.techName ?? "A technician"} is chatting with this customer
                        </div>
                        <div className="fs-8 text-gray-600">
                            {session.jobNumber ? `Job #${session.jobNumber} · ` : ""}
                            Started {toPrettyDate(session.startedAt)}
                        </div>
                    </div>
                </FlexRow>

                <button className="btn btn-sm btn-light-danger"
                    disabled={ending}
                    onClick={end}>
                    {ending
                        ? <Icon icon="spinner-third" spin />
                        : <>End Chat</>
                    }
                </button>
            </FlexRow>
        </div>
    )
}
